// http.service.ts
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, catchError, map } from 'rxjs';
import { ApiResponse } from '@nebula-shared/domain';
import { API_BASE_URL_TOKEN } from '../auth.token';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL_TOKEN, { optional: true });

  private getBaseUrl(): string {
    if (!this.apiBaseUrl) {
      return '/api';
    }
    return this.apiBaseUrl;
  }

  get<T>(
    endpoint: string,
    params?: Record<string, unknown>,
    headers?: HttpHeaders,
  ): Observable<ApiResponse<T>> {
    const baseUrl = this.getBaseUrl();
    let httpParams = new HttpParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          httpParams = httpParams.set(key, String(value));
        }
      });
    }

    const options: { params: HttpParams; headers?: HttpHeaders } = { params: httpParams };

    if (headers) {
      options.headers = headers;
    }

    return this.http.get<ApiResponse<T>>(`${baseUrl}${endpoint}`, options);
  }

  post<T>(
    endpoint: string,
    body: unknown,
    params?: Record<string, unknown>,
    headers?: HttpHeaders,
  ): Observable<ApiResponse<T>> {
    const baseUrl = this.getBaseUrl();
    const options: { params?: HttpParams; headers?: HttpHeaders } = {};

    if (params && Object.keys(params).length > 0) {
      let httpParams = new HttpParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          httpParams = httpParams.set(key, String(value));
        }
      });
      options.params = httpParams;
    }

    if (headers) {
      options.headers = headers;
    }

    return this.http
      .post<ApiResponse<T>>(`${baseUrl}${endpoint}`, body, options)
      .pipe(catchError((error: unknown) => throwError(() => error)));
  }

  put<T>(endpoint: string, body: unknown, headers?: HttpHeaders): Observable<ApiResponse<T>> {
    const baseUrl = this.getBaseUrl();
    const options = headers ? { headers } : {};
    return this.http.put<ApiResponse<T>>(`${baseUrl}${endpoint}`, body, options);
  }

  patch<T>(endpoint: string, body: unknown): Observable<ApiResponse<T>> {
    const baseUrl = this.getBaseUrl();
    return this.http.patch<ApiResponse<T>>(`${baseUrl}${endpoint}`, body);
  }

  delete<T>(endpoint: string): Observable<ApiResponse<T>> {
    const baseUrl = this.getBaseUrl();
    return this.http.delete<ApiResponse<T>>(`${baseUrl}${endpoint}`);
  }

  getContent<T>(
    endpoint: string,
    params?: Record<string, string>,
    headers?: HttpHeaders,
  ): Observable<T> {
    return this.get<T>(endpoint, params, headers).pipe(
      map((response) => response.response.content),
    );
  }

  postContent<T>(
    endpoint: string,
    body: unknown,
    params?: Record<string, unknown>,
    headers?: HttpHeaders,
  ): Observable<T> {
    return this.post<T>(endpoint, body, params, headers).pipe(
      map((response) => response.response.content),
    );
  }

  putContent<T>(endpoint: string, body: unknown, headers?: HttpHeaders): Observable<T> {
    return this.put<T>(endpoint, body, headers).pipe(map((response) => response.response.content));
  }

  patchContent<T>(endpoint: string, body: unknown): Observable<T> {
    return this.patch<T>(endpoint, body).pipe(map((response) => response.response.content));
  }

  deleteContent<T>(endpoint: string): Observable<T> {
    return this.delete<T>(endpoint).pipe(map((response) => response.response.content));
  }
}
